const DECISION_STATE_LABELS = {
  new: "Neu",
  review: "In Pruefung",
  shortlist: "Interessant",
  rejected: "Abgelehnt",
  hired: "Eingestellt",
};

const AD_STATUS_LABELS = {
  draft: "Entwurf",
  published: "Veroeffentlicht",
  active: "Aktiv",
  paused: "Pausiert",
  archived: "Archiviert",
};

const DOCUMENT_STATUS_META = {
  missing: { label: "fehlt", color: "danger", weight: 0 },
  requested: { label: "angefragt", color: "warning", weight: 1 },
  uploaded: { label: "hochgeladen", color: "info", weight: 2 },
  in_review: { label: "in Pruefung", color: "warning", weight: 3 },
  approved: { label: "freigegeben", color: "success", weight: 4 },
  insufficient: { label: "unzureichend", color: "danger", weight: 1 },
  expired: { label: "abgelaufen", color: "danger", weight: 1 },
  not_required: { label: "nicht erforderlich", color: "secondary", weight: 4 },
};

const CONSENT_STATUS_META = {
  none: { label: "keine Freigabe", color: "secondary" },
  requested: { label: "Freigabe angefragt", color: "warning" },
  granted: { label: "Freigabe erteilt", color: "success" },
  revoked: { label: "Freigabe widerrufen", color: "danger" },
};

const CASE_STATUS_META = {
  incomplete: { label: "unvollstaendig", color: "danger" },
  unclear: { label: "unklar", color: "warning" },
  consent_missing: { label: "freigaben fehlen", color: "warning" },
  partially_ready: { label: "teilweise ready", color: "info" },
  filing_ready: { label: "filing-ready", color: "success" },
};

const REQUEST_ACTIVE_STATUSES = new Set(["draft", "sent", "viewed", "answered"]);
const REQUEST_DONE_STATUSES = new Set(["fulfilled", "obsolete", "expired"]);

function normalizeText(value) {
  return String(value || "").trim();
}

function normalizeToken(value) {
  return normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function hasText(value) {
  return normalizeText(value).length > 0;
}

function asArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function formatDate(value) {
  if (!value) return "k.A.";
  const next = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(next.getTime())) return "k.A.";
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" }).format(next);
}

export function formatDateTime(value) {
  if (!value) return "k.A.";
  const next = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(next.getTime())) return "k.A.";
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(next);
}

function getDocumentStatusMeta(status) {
  return DOCUMENT_STATUS_META[status] || DOCUMENT_STATUS_META.missing;
}

function getConsentStatusMeta(status) {
  return CONSENT_STATUS_META[status] || CONSENT_STATUS_META.none;
}

function getCaseStatusMeta(status) {
  return CASE_STATUS_META[status] || CASE_STATUS_META.incomplete;
}

export function getDocumentStatusColor(status) {
  return getDocumentStatusMeta(status).color;
}

export function getConsentStatusColor(status) {
  return getConsentStatusMeta(status).color;
}

export function getCaseStatusColor(status) {
  return getCaseStatusMeta(status).color;
}

export function formatDocumentStatusLabel(status) {
  return getDocumentStatusMeta(status).label;
}

export function formatConsentStatusLabel(status) {
  return getConsentStatusMeta(status).label;
}

export function formatCaseStatusLabel(status) {
  return getCaseStatusMeta(status).label;
}

export function formatDecisionState(value) {
  return DECISION_STATE_LABELS[String(value || "").toLowerCase()] || value || "Neu";
}

export function formatAdStatusLabel(value) {
  return AD_STATUS_LABELS[String(value || "").toLowerCase()] || value || "Unbekannt";
}

function deriveGateItems(profile = {}) {
  return [
    {
      key: "housing_support",
      label: "Wohnungsunterstuetzung",
      ready: Boolean(profile.housing_support_confirmed) && hasText(profile.housing_support_mode),
      note: profile.housing_support_confirmed
        ? `${profile.housing_support_mode || "Unterstuetzungsmodus offen"} ist dokumentiert.`
        : "Es fehlt eine belastbare Aussage zu Unterkunft oder Wohnungsunterstuetzung.",
    },
    {
      key: "salary_bridge",
      label: "Ueberbrueckung bis erstes Gehalt",
      ready: Boolean(profile.payroll_bridge_ready) && hasText(profile.first_salary_bridge_plan),
      note: profile.payroll_bridge_ready
        ? "Ein Plan fuer die finanzielle Ueberbrueckung bis zum ersten Gehalt ist dokumentiert."
        : "Die Gehaltsbruecke fuer die ersten Wochen ist noch nicht sauber beschrieben.",
    },
    {
      key: "integration_owner",
      label: "Integration Owner",
      ready: hasText(profile.integration_owner_name) && hasText(profile.integration_owner_email),
      note: hasText(profile.integration_owner_name)
        ? `${profile.integration_owner_name} ist als Ansprechpartner sichtbar.`
        : "Es fehlt ein benannter Ansprechpartner fuer Arrival und Integration.",
    },
    {
      key: "team_preparedness",
      label: "Team- und Stationsvorbereitung",
      ready: Boolean(profile.team_ready) && hasText(profile.team_preparedness_note),
      note: profile.team_ready
        ? "Die Teamvorbereitung ist als operativer Arbeitsstand dokumentiert."
        : "Der Einsatzbereich oder das Team ist noch nicht sichtbar vorbereitet.",
    },
    {
      key: "compensation_transparency",
      label: "Transparenz zu Verguetung und Einsatz",
      ready: Boolean(profile.compensation_transparent) && hasText(profile.salary_transparency_note),
      note: profile.compensation_transparent
        ? "Verguetung, Einsatz und Erwartungshaltung sind transparent beschrieben."
        : "Transparente Angaben zu Gehalt, Einsatz und Bedingungen fehlen noch.",
    },
    {
      key: "arrival_support",
      label: "Arrival- und Onboarding-Logik",
      ready: hasText(profile.arrival_support_note) || hasText(profile.accommodation_note),
      note: hasText(profile.arrival_support_note) || hasText(profile.accommodation_note)
        ? "Arrival- und Onboarding-Hinweise sind dokumentiert."
        : "Arrival-Unterstuetzung ist noch nicht operativ vorbereitet.",
    },
  ];
}

export function buildGateSummary(profile = {}) {
  const items = deriveGateItems(profile);
  const readyCount = items.filter((item) => item.ready).length;
  const totalCount = items.length;
  const status = readyCount === totalCount
    ? "complete"
    : readyCount >= Math.ceil(totalCount / 2)
      ? "review"
      : "incomplete";

  return {
    status,
    statusLabel: status === "complete" ? "Gate bereit" : status === "review" ? "Gate in Arbeit" : "Gate offen",
    readyCount,
    totalCount,
    items,
  };
}

function unwrapDetail(detail) {
  if (!detail) return null;
  if (detail.data && !detail.decision_preparation) return detail.data || null;
  return detail;
}

function unwrapFiling(filing) {
  if (!filing) return null;
  if (filing.kit && !filing.documents) return filing.kit || null;
  return filing;
}

function hasOriginalAccess(documentEntry) {
  return Boolean(documentEntry?.download_url || documentEntry?.local_path);
}

function buildDocSearchIndex(filing) {
  return {
    documents: asArray(filing?.documents),
    requests: asArray(filing?.candidate_request_board?.all_requests),
  };
}

function matchesNeedles(candidateText, needles = []) {
  const haystack = normalizeToken(candidateText);
  if (!haystack) return false;
  return needles.some((needle) => haystack.includes(normalizeToken(needle)));
}

function findBestDocument(documents, requirement) {
  const keywords = asArray(requirement.match?.keywords);
  const ids = asArray(requirement.match?.ids);
  const responsibility = requirement.responsibility;
  let best = null;
  let bestScore = -1;

  documents.forEach((entry) => {
    const idToken = normalizeToken(entry.id);
    const titleToken = normalizeToken(entry.title);
    const provider = normalizeToken(entry.who_provides_it || entry.responsibility?.key);
    let score = 0;

    ids.forEach((needle) => {
      const token = normalizeToken(needle);
      if (idToken === token) score += 6;
      if (idToken.includes(token)) score += 5;
    });

    keywords.forEach((needle) => {
      const token = normalizeToken(needle);
      if (titleToken.includes(token)) score += 3;
      if (idToken.includes(token)) score += 2;
    });

    if (responsibility && provider && provider.includes(normalizeToken(responsibility))) {
      score += 1;
    }

    if (score > bestScore) {
      best = entry;
      bestScore = score;
    }
  });

  return bestScore > 0 ? best : null;
}

function findMatchingRequests(requests, requirement) {
  const keywords = asArray(requirement.match?.keywords);
  return requests.filter((entry) => {
    const text = [entry.title, entry.description, entry.request_reason].filter(Boolean).join(" ");
    return matchesNeedles(text, keywords);
  });
}

function deriveVerificationStatus({ count, byStatus }) {
  if (toNumber(byStatus?.rejected) > 0 || toNumber(byStatus?.suspicious) > 0 || toNumber(byStatus?.inconsistent) > 0) {
    return "insufficient";
  }
  if (toNumber(byStatus?.online_verified) > 0 || toNumber(byStatus?.formally_checked) > 0) {
    return "approved";
  }
  if (toNumber(byStatus?.pending_review) > 0) {
    return "in_review";
  }
  if (count > 0 || toNumber(byStatus?.uploaded) > 0 || toNumber(byStatus?.declared) > 0) {
    return "uploaded";
  }
  return "missing";
}

function mapReviewBucketToStatus(documentEntry, activeRequestCount) {
  const bucket = String(documentEntry?.review_bucket || "").toLowerCase();
  if (bucket === "missing_docs") return activeRequestCount > 0 ? "requested" : "missing";
  if (bucket === "prepared_but_incomplete") return "insufficient";
  if (bucket === "ready_to_sign") return "in_review";
  if (bucket === "ready_to_upload") return "approved";
  if (bucket === "manual_only") {
    if (activeRequestCount > 0) return "requested";
    return hasOriginalAccess(documentEntry) ? "uploaded" : "missing";
  }
  if (hasOriginalAccess(documentEntry)) return "uploaded";
  return activeRequestCount > 0 ? "requested" : "missing";
}

function applyFreshnessToStatus(status, documentEntry) {
  const freshnessStatus = String(documentEntry?.freshness?.freshness_status || documentEntry?.freshness_status || "").toLowerCase();
  if (["local_unknown_currency", "local_update_unclear"].includes(freshnessStatus) && !["missing", "requested", "not_required"].includes(status)) {
    return "expired";
  }
  return status;
}

function deriveConsentStatus({ needsConsent, matchedDocument, status, activeRequestCount }) {
  if (!needsConsent || status === "not_required") return "none";
  if (matchedDocument?.consent_status === "granted") return "granted";
  if (matchedDocument?.consent_status === "revoked") return "revoked";
  if (hasOriginalAccess(matchedDocument)) return "granted";
  if (activeRequestCount > 0) return "requested";
  return "none";
}

function buildStatusFromSignals({
  required,
  matchedDocument,
  signalStatus,
  activeRequestCount,
}) {
  if (!required) return "not_required";

  const explicit = matchedDocument ? mapReviewBucketToStatus(matchedDocument, activeRequestCount) : "";
  const baseStatus = explicit || signalStatus || (activeRequestCount > 0 ? "requested" : "missing");
  return applyFreshnessToStatus(baseStatus, matchedDocument);
}

function summarizeRequestState(requests) {
  const active = requests.filter((entry) => REQUEST_ACTIVE_STATUSES.has(String(entry.status || "").toLowerCase()));
  const completed = requests.filter((entry) => REQUEST_DONE_STATUSES.has(String(entry.status || "").toLowerCase()));
  return {
    active,
    completed,
    activeCount: active.length,
    completedCount: completed.length,
  };
}

function buildRecognitionRequirement(classification = {}) {
  const status = String(classification.recognition_status || "");
  if (!classification.recognition_required && !classification.regulated_profession) {
    return {
      signalStatus: "not_required",
      recognitionLabel: "nicht erforderlich",
      note: "Der Fall liegt nicht im anerkennungspflichtigen Pfad.",
    };
  }
  if (status === "full") {
    return {
      signalStatus: "approved",
      recognitionLabel: "anerkannt",
      note: "Der Anerkennungsstatus ist als voll hinterlegt.",
    };
  }
  if (status === "open") {
    return {
      signalStatus: "in_review",
      recognitionLabel: "in Pruefung",
      note: "Die Anerkennung ist noch in Pruefung.",
    };
  }
  if (["partial", "partnership"].includes(status)) {
    return {
      signalStatus: "insufficient",
      recognitionLabel: "Defizitbescheid",
      note: "Es liegt nur eine Teilanerkennung oder ein Defizit-/Partnerschaftspfad vor.",
    };
  }
  return {
    signalStatus: "missing",
    recognitionLabel: "nicht vorhanden",
    note: "Ein belastbarer Anerkennungsstatus ist noch nicht hinterlegt.",
  };
}

function buildLanguageRequirementSignal(detail, filing) {
  const languageSignal = detail?.decision_handoff?.language_test_b1_core || {};
  const languageAlignment = detail?.decision_handoff?.language_evidence_alignment || {};
  const verificationReferences = detail?.decision_handoff?.verification?.references || {};
  const byStatus = verificationReferences.by_status || {};
  const certificateCount = toNumber(verificationReferences.by_type?.language_certificate);
  const verificationStatus = deriveVerificationStatus({ count: certificateCount, byStatus });
  const contradiction = Boolean(languageAlignment?.contradiction_flag);
  const stale = Boolean(languageAlignment?.staleness_flag);

  if (contradiction) {
    return {
      signalStatus: "insufficient",
      note: languageAlignment.summary_de || "Sprachsignal und Nachweise widersprechen sich.",
    };
  }
  if (verificationStatus !== "missing") {
    return {
      signalStatus: stale && verificationStatus === "approved" ? "expired" : verificationStatus,
      note: languageAlignment.summary_de || "Sprachnachweis wurde aus dem vorhandenen Evidence-Pfad abgeleitet.",
    };
  }
  if (languageSignal?.shared && languageSignal?.signal?.estimated_cefr_band) {
    return {
      signalStatus: stale ? "expired" : "uploaded",
      note: languageAlignment.summary_de || `Ein internes Sprachsignal (${languageSignal.signal.estimated_cefr_band}) ist geteilt.`,
    };
  }
  if (filing?.classification?.regulated_profession) {
    return {
      signalStatus: "missing",
      note: "Fuer den reglementierten Pfad fehlt noch ein belastbarer Sprachnachweis.",
    };
  }
  return {
    signalStatus: "missing",
    note: languageAlignment.summary_de || "Es liegt noch kein belastbarer Sprachnachweis vor.",
  };
}

function buildCvSignal(candidateCore = {}) {
  if (candidateCore.cv_core_ready) {
    return {
      signalStatus: "approved",
      note: "Das Kandidatenprofil enthaelt einen tragenden, strukturierten Lebenslaufkern.",
    };
  }
  if (candidateCore.profile_basis_ready || candidateCore.education?.present || candidateCore.experience?.present) {
    return {
      signalStatus: "uploaded",
      note: "Teile des Lebenslaufs sind vorhanden, die Lueckenfreiheit ist aber noch nicht belastbar bestaetigt.",
    };
  }
  return {
    signalStatus: "missing",
    note: "Es liegt noch kein belastbarer, lueckenloser Lebenslaufkern vor.",
    };
}

function deriveEmployerDocumentSignal(ad, requirementKey) {
  const description = normalizeText(ad?.beschreibung || ad?.beruf_text || ad?.jobbeschreibung || "");
  if (requirementKey === "job_description") {
    if (description.length >= 240) {
      return {
        signalStatus: "approved",
        note: "Die Stellenbeschreibung ist strukturiert im Jobprofil vorhanden.",
      };
    }
    if (description.length >= 80) {
      return {
        signalStatus: "uploaded",
        note: "Es existiert eine Beschreibung, sie wirkt aber noch nicht ausreichend detailliert.",
      };
    }
    if (description.length > 0) {
      return {
        signalStatus: "insufficient",
        note: "Die Stellenbeschreibung ist vorhanden, aber zu knapp fuer eine belastbare Filing-Einordnung.",
      };
    }
    return {
      signalStatus: "missing",
      note: "Es fehlt eine detaillierte Stellenbeschreibung.",
    };
  }

  if (requirementKey === "employment_offer_contract") {
    const hasContractClue = hasText(ad?.arbeitsvertrag_ref) || hasText(ad?.angebot_ref) || hasText(ad?.contract_reference);
    if (hasContractClue) {
      return {
        signalStatus: "uploaded",
        note: "Im Stellenkontext ist ein Arbeitsvertrags- oder Angebotsverweis sichtbar.",
      };
    }
    return {
      signalStatus: "missing",
      note: "Arbeitsvertrag oder verbindliches Angebot sind noch nicht hinterlegt.",
    };
  }

  return {
    signalStatus: "missing",
    note: "Die Arbeitgeberunterlage ist noch nicht hinterlegt.",
  };
}

function buildRequirementBlueprint({ ad, detail, filing, row }) {
  const classification = filing?.classification || {};
  const candidateCore = detail?.decision_handoff?.candidate_core || {};
  const verificationReferences = detail?.decision_handoff?.verification?.references || {};
  const verificationTypes = verificationReferences.by_type || {};
  const verificationByStatus = verificationReferences.by_status || {};
  const docSearch = buildDocSearchIndex(filing);
  const recognitionSignal = buildRecognitionRequirement(classification);
  const languageSignal = buildLanguageRequirementSignal(detail, filing);
  const cvSignal = buildCvSignal(candidateCore);
  const hasEducationEvidence = toNumber(verificationTypes.education_certificate) > 0;
  const hasWorkEvidence = toNumber(verificationTypes.work_reference) > 0;
  const hasPassportEvidence = Boolean(verificationReferences.passport_copy_present) || toNumber(verificationTypes.passport_copy) > 0;

  const sharedEducationStatus = deriveVerificationStatus({
    count: toNumber(verificationTypes.education_certificate),
    byStatus: verificationByStatus,
  });
  const sharedWorkStatus = deriveVerificationStatus({
    count: toNumber(verificationTypes.work_reference),
    byStatus: verificationByStatus,
  });
  const sharedPassportStatus = deriveVerificationStatus({
    count: toNumber(verificationTypes.passport_copy),
    byStatus: verificationByStatus,
  });

  const likelyNeedsTranslations = Boolean(classification.regulated_profession || classification.recognition_required || classification.fast_track);
  const requiresTranscript = Boolean(classification.regulated_profession || classification.recognition_required);
  const requiresExperience = Boolean(candidateCore.experience?.present || classification.regulated_profession || hasWorkEvidence);
  const requiresProfessionPermit = Boolean(classification.regulated_profession);
  const requiresCompanyProof = Boolean(classification.fast_track || classification.regulated_profession);
  const requiresPoA = Boolean(classification.fast_track || classification.power_of_attorney_required);

  const requirements = [
    {
      key: "power_of_attorney",
      section: "Arbeitgeber-Seite",
      title: "Vollmacht der Fachkraft",
      responsibility: "employer",
      required: requiresPoA,
      critical: requiresPoA,
      needsConsent: false,
      match: { ids: ["power-of-attorney", "vollmacht"], keywords: ["vollmacht", "power of attorney"] },
      signalStatus: requiresPoA ? "missing" : "not_required",
      note: requiresPoA
        ? "Im beschleunigten Verfahren ist die Vollmacht zwingender Case-Bestandteil."
        : "Ohne beschleunigten Vollmachtspfad ist diese Unterlage aktuell nicht erforderlich.",
    },
    {
      key: "employment_offer_contract",
      section: "Arbeitgeber-Seite",
      title: "Arbeitsvertrag oder verbindliches Angebot",
      responsibility: "employer",
      required: true,
      critical: true,
      needsConsent: false,
      match: { ids: ["employment-contract", "contract", "offer"], keywords: ["arbeitsvertrag", "angebot", "employment contract", "offer"] },
      ...deriveEmployerDocumentSignal(ad, "employment_offer_contract"),
    },
    {
      key: "job_description",
      section: "Arbeitgeber-Seite",
      title: "Detaillierte Stellenbeschreibung",
      responsibility: "employer",
      required: true,
      critical: true,
      needsConsent: false,
      match: { ids: ["job-description", "stellenbeschreibung"], keywords: ["stellenbeschreibung", "job description"] },
      ...deriveEmployerDocumentSignal(ad, "job_description"),
    },
    {
      key: "company_proof",
      section: "Arbeitgeber-Seite",
      title: "Unternehmensnachweise",
      responsibility: "employer",
      required: requiresCompanyProof,
      critical: requiresCompanyProof,
      needsConsent: false,
      match: { ids: ["company-proof", "company-document"], keywords: ["unternehmens", "handelsregister", "company proof", "registration"] },
      signalStatus: requiresCompanyProof ? "missing" : "not_required",
      note: requiresCompanyProof
        ? "Fuer den aktiven Filing-Pfad werden belastbare Unternehmensnachweise erwartet."
        : "Im aktuellen Pfad ist kein gesonderter Unternehmensnachweis markiert.",
    },
    {
      key: "passport",
      section: "Fachkraft-Seite",
      title: "Reisepass",
      responsibility: "candidate",
      required: true,
      critical: true,
      needsConsent: true,
      match: { ids: ["passport-copy", "candidate-evidence-passport-copy"], keywords: ["reisepass", "passport", "passkopie"] },
      signalStatus: hasPassportEvidence ? sharedPassportStatus : "missing",
      note: hasPassportEvidence
        ? "Passnachweise sind im Verification-Pfad sichtbar."
        : "Die Passkopie fehlt im Arbeitgebersichtpfad noch vollstaendig.",
    },
    {
      key: "degree_or_studies",
      section: "Fachkraft-Seite",
      title: "Abschluss / Studium",
      responsibility: "candidate",
      required: true,
      critical: true,
      needsConsent: true,
      match: { ids: ["candidate-evidence-education-certificate"], keywords: ["abschluss", "studium", "education", "degree"] },
      signalStatus: hasEducationEvidence ? sharedEducationStatus : "missing",
      note: hasEducationEvidence
        ? "Mindestens ein Bildungsnachweis ist im Verification-Pfad sichtbar."
        : "Es fehlt ein belastbarer Abschluss- oder Studiennachweis.",
    },
    {
      key: "certificates_and_diplomas",
      section: "Fachkraft-Seite",
      title: "Zeugnisse / Diplome",
      responsibility: "candidate",
      required: true,
      critical: true,
      needsConsent: true,
      match: { ids: ["certificate", "diploma"], keywords: ["zeugnis", "diplom", "certificate", "diploma"] },
      signalStatus: toNumber(verificationTypes.education_certificate) > 1
        ? sharedEducationStatus
        : hasEducationEvidence
          ? "uploaded"
          : "missing",
      note: toNumber(verificationTypes.education_certificate) > 1
        ? "Mehrere Bildungsnachweise deuten auf Zeugnisse und Diplome im Fall hin."
        : hasEducationEvidence
          ? "Es gibt Bildungsnachweise, die Vollstaendigkeit von Zeugnissen und Diplomen ist aber noch nicht abschliessend sichtbar."
          : "Zeugnisse und Diplome fehlen im sichtbaren Filing-Pfad.",
    },
    {
      key: "transcript",
      section: "Fachkraft-Seite",
      title: "Notenuebersicht",
      responsibility: "candidate",
      required: requiresTranscript,
      critical: requiresTranscript,
      needsConsent: true,
      match: { ids: ["transcript", "notenuebersicht"], keywords: ["notenuebersicht", "transcript", "grades"] },
      signalStatus: requiresTranscript
        ? (toNumber(verificationTypes.education_certificate) > 1 ? "uploaded" : "missing")
        : "not_required",
      note: requiresTranscript
        ? "Im Anerkennungs- oder reglementierten Pfad ist eine Notenuebersicht oft verfahrensrelevant."
        : "Im aktuellen Pfad ist keine separate Notenuebersicht markiert.",
    },
    {
      key: "translations",
      section: "Fachkraft-Seite",
      title: "Uebersetzungen",
      responsibility: "candidate",
      required: likelyNeedsTranslations,
      critical: likelyNeedsTranslations,
      needsConsent: true,
      match: { ids: ["translation", "translations"], keywords: ["uebersetzung", "translation"] },
      signalStatus: likelyNeedsTranslations ? "missing" : "not_required",
      note: likelyNeedsTranslations
        ? "Fremdsprachige Unterlagen muessen als uebersetzte Version verfahrensfest vorliegen."
        : "Im aktuellen Pfad ist keine gesonderte Uebersetzungspflicht markiert.",
    },
    {
      key: "gapless_cv",
      section: "Fachkraft-Seite",
      title: "Lebenslauf (lueckenlos)",
      responsibility: "candidate",
      required: true,
      critical: true,
      needsConsent: true,
      match: { ids: ["cv", "resume"], keywords: ["lebenslauf", "cv", "resume"] },
      signalStatus: cvSignal.signalStatus,
      note: cvSignal.note,
    },
    {
      key: "work_experience",
      section: "Fachkraft-Seite",
      title: "Berufserfahrung",
      responsibility: "candidate",
      required: requiresExperience,
      critical: requiresExperience,
      needsConsent: true,
      match: { ids: ["candidate-evidence-work-reference"], keywords: ["berufserfahrung", "arbeitsnachweis", "work reference", "experience"] },
      signalStatus: requiresExperience
        ? (hasWorkEvidence ? sharedWorkStatus : "missing")
        : "not_required",
      note: requiresExperience
        ? (hasWorkEvidence
          ? "Arbeits- oder Erfahrungsnachweise sind im Verification-Pfad sichtbar."
          : "Relevante Berufserfahrung ist signalisiert, aber noch nicht belegt.")
        : "Im aktuellen Fall ist kein gesonderter Erfahrungsnachweis markiert.",
    },
    {
      key: "language_certificate",
      section: "Fachkraft-Seite",
      title: "Sprachnachweis",
      responsibility: "candidate",
      required: true,
      critical: true,
      needsConsent: true,
      match: { ids: ["candidate-evidence-language-certificate"], keywords: ["sprachnachweis", "sprachzertifikat", "language certificate", "language"] },
      signalStatus: languageSignal.signalStatus,
      note: languageSignal.note,
    },
    {
      key: "recognition_status",
      section: "Fachkraft-Seite",
      title: "Anerkennungsstatus",
      responsibility: "candidate",
      required: Boolean(classification.recognition_required || classification.regulated_profession),
      critical: Boolean(classification.recognition_required || classification.regulated_profession),
      needsConsent: false,
      match: { ids: ["recognition", "anerkennung"], keywords: ["anerkennung", "recognition"] },
      signalStatus: recognitionSignal.signalStatus,
      note: recognitionSignal.note,
      recognitionLabel: recognitionSignal.recognitionLabel,
    },
    {
      key: "profession_permit",
      section: "Zusatz reglementierter Beruf",
      title: "Berufserlaubnis / Anerkennungsbescheid / Defizit",
      responsibility: "candidate",
      required: requiresProfessionPermit,
      critical: requiresProfessionPermit,
      needsConsent: true,
      match: { ids: ["berufserlaubnis", "anerkennungsbescheid", "defizit"], keywords: ["berufserlaubnis", "anerkennungsbescheid", "defizit", "recognition"] },
      signalStatus: requiresProfessionPermit ? recognitionSignal.signalStatus : "not_required",
      note: requiresProfessionPermit
        ? "Reglementierte Berufe brauchen einen belastbaren Erlaubnis- oder Anerkennungsstand."
        : "Kein reglementierter Zusatznachweis erforderlich.",
    },
  ];

  return requirements.map((requirement) => {
    const matchedDocument = findBestDocument(docSearch.documents, requirement);
    const requestState = summarizeRequestState(findMatchingRequests(docSearch.requests, requirement));
    const documentStatus = buildStatusFromSignals({
      required: requirement.required,
      matchedDocument,
      signalStatus: requirement.signalStatus,
      activeRequestCount: requestState.activeCount,
    });
    const consentStatus = deriveConsentStatus({
      needsConsent: requirement.needsConsent,
      matchedDocument,
      status: documentStatus,
      activeRequestCount: requestState.activeCount,
    });
    const availableOriginal = Boolean(hasOriginalAccess(matchedDocument) && (!requirement.needsConsent || consentStatus === "granted"));
    const verificationSummary = [
      requirement.recognitionLabel || "",
      matchedDocument?.review_bucket ? `Review ${String(matchedDocument.review_bucket).replace(/_/g, " ")}` : "",
      matchedDocument?.freshness?.label || "",
    ].filter(Boolean).join(" · ");

    return {
      id: `${row?.id || "case"}-${requirement.key}`,
      key: requirement.key,
      section: requirement.section,
      title: requirement.title,
      responsibility: requirement.responsibility,
      required: requirement.required,
      critical: requirement.critical,
      needsConsent: requirement.needsConsent,
      status: documentStatus,
      statusLabel: formatDocumentStatusLabel(documentStatus),
      statusColor: getDocumentStatusColor(documentStatus),
      consentStatus,
      consentStatusLabel: formatConsentStatusLabel(consentStatus),
      consentStatusColor: getConsentStatusColor(consentStatus),
      availableOriginal,
      verificationStatus: verificationSummary || requirement.note || "Noch keine verfahrensfeste Evidenz sichtbar.",
      note: requirement.note,
      recognitionLabel: requirement.recognitionLabel || "",
      nextAction: documentStatus === "missing"
        ? "Pflichtunterlage beschaffen"
        : documentStatus === "requested"
          ? "Anforderung und Ruecklauf nachhalten"
          : documentStatus === "uploaded"
            ? "Unterlage pruefen und freigeben"
            : documentStatus === "in_review"
              ? "Pruefung abschliessen"
              : documentStatus === "insufficient"
                ? "Unterlage korrigieren oder ergaenzen"
                : documentStatus === "expired"
                  ? "Aktuelle Fassung anfordern"
                  : documentStatus === "approved" && requirement.needsConsent && consentStatus !== "granted"
                    ? "Consent fuer Originalzugriff einholen"
                    : "Keine offene Aktion",
      matchedDocument,
      requestState,
    };
  });
}

function buildScorecard(requirements, caseStatus, detail) {
  const requiredDocs = requirements.filter((entry) => entry.required);
  const approvedDocs = requiredDocs.filter((entry) => entry.status === "approved").length;
  const clearDocs = requiredDocs.filter((entry) => ["approved", "not_required"].includes(entry.status)).length;
  const consentComplete = requiredDocs.filter((entry) => !entry.needsConsent || entry.consentStatus === "granted").length;
  const language = requirements.find((entry) => entry.key === "language_certificate");
  const qualificationKeys = new Set(["degree_or_studies", "certificates_and_diplomas", "work_experience", "recognition_status", "profession_permit"]);
  const qualificationDocs = requiredDocs.filter((entry) => qualificationKeys.has(entry.key));
  const decisionState = String(detail?.decision_preparation?.decision_state || detail?.application_status || "").toLowerCase();
  const decisionNote = normalizeText(detail?.decision_preparation?.decision_note);

  const documentsScore = requiredDocs.length ? Math.round((clearDocs / requiredDocs.length) * 4) : 4;
  const languageScore = !language || language.status === "not_required"
    ? 4
    : language.status === "approved"
      ? 4
      : language.status === "in_review"
        ? 3
        : language.status === "uploaded"
          ? 2
          : language.status === "requested"
            ? 1
            : 0;
  const qualificationReady = qualificationDocs.filter((entry) => ["approved", "not_required"].includes(entry.status)).length;
  const qualificationScore = qualificationDocs.length ? Math.round((qualificationReady / qualificationDocs.length) * 4) : 4;
  const consentScore = requiredDocs.length ? Math.round((consentComplete / requiredDocs.length) * 4) : 4;
  const filingScore = caseStatus === "filing_ready"
    ? 4
    : caseStatus === "partially_ready"
      ? 3
      : caseStatus === "unclear"
        ? 2
        : caseStatus === "consent_missing"
          ? 1
          : 0;
  const decisionScore = decisionNote
    ? decisionState && decisionState !== "new"
      ? 4
      : 2
    : 0;

  const criteria = [
    { key: "documents", label: "Unterlagen", score: documentsScore, maxScore: 4 },
    { key: "qualification", label: "Qualifikation", score: qualificationScore, maxScore: 4 },
    { key: "language", label: "Sprache", score: languageScore, maxScore: 4 },
    { key: "consent", label: "Freigaben", score: consentScore, maxScore: 4 },
    { key: "filing", label: "Readiness", score: filingScore, maxScore: 4 },
    { key: "decision", label: "Entscheidung", score: decisionScore, maxScore: 4 },
  ];
  const totalScore = criteria.reduce((sum, item) => sum + item.score, 0);
  const maxScore = criteria.reduce((sum, item) => sum + item.maxScore, 0);
  const percent = maxScore ? Math.round((totalScore / maxScore) * 100) : 0;

  return {
    totalScore,
    maxScore,
    percent,
    bandLabel: percent >= 85 ? "stark" : percent >= 60 ? "stabil" : percent >= 35 ? "fragil" : "kritisch",
    approvedDocuments: approvedDocs,
    requiredDocuments: requiredDocs.length,
    criteria,
  };
}

function summarizeCaseStatus({ requirements, filing, detail, row }) {
  const requiredDocs = requirements.filter((entry) => entry.required);
  const hardMissing = requiredDocs.filter((entry) => ["missing", "insufficient", "expired"].includes(entry.status));
  const consentGaps = requiredDocs.filter((entry) => entry.needsConsent && entry.consentStatus !== "granted" && !["missing", "requested", "not_required"].includes(entry.status));
  const pendingReview = requiredDocs.filter((entry) => ["uploaded", "in_review", "requested"].includes(entry.status));
  const packBlockers = toNumber(filing?.pack_ops_state?.blocker_count);
  const packWarnings = toNumber(filing?.pack_ops_state?.warning_count);
  const activeRequests = asArray(filing?.candidate_request_board?.all_requests).filter((entry) => REQUEST_ACTIVE_STATUSES.has(String(entry.status || "").toLowerCase()));
  const decisionNoteMissing = String(detail?.decision_preparation?.decision_state || row?.status || "").toLowerCase() !== "new"
    && !hasText(detail?.decision_preparation?.decision_note);

  const blockerReasons = [
    ...hardMissing.map((entry) => `${entry.title}: ${entry.statusLabel}`),
    ...consentGaps.map((entry) => `${entry.title}: ${entry.consentStatusLabel}`),
    filing?.pack_ops_state?.dominant_blocker?.detail || filing?.pack_ops_state?.dominant_blocker?.title || "",
    decisionNoteMissing ? "Entscheidungsnotiz fuer den aktuellen Entscheidungsstatus fehlt." : "",
  ].filter(Boolean);

  let caseStatus = "filing_ready";
  if (hardMissing.length > 0) {
    caseStatus = "incomplete";
  } else if (consentGaps.length > 0) {
    caseStatus = "consent_missing";
  } else if (pendingReview.length > 0 || packBlockers > 0 || decisionNoteMissing) {
    caseStatus = "unclear";
  } else if (packWarnings > 0 || activeRequests.length > 0) {
    caseStatus = "partially_ready";
  }

  return {
    caseStatus,
    caseStatusLabel: formatCaseStatusLabel(caseStatus),
    caseStatusColor: getCaseStatusColor(caseStatus),
    hardMissing,
    consentGaps,
    pendingReview,
    packBlockers,
    packWarnings,
    activeRequests,
    blockerReasons,
  };
}

function buildNextAction(statusSummary, filing, requirements) {
  if (statusSummary.hardMissing.length > 0) {
    const requestedItem = statusSummary.hardMissing.find((entry) => entry.status === "requested");
    return requestedItem
      ? `Pflichtunterlagen nachhalten: ${requestedItem.title}`
      : `Pflichtunterlagen schliessen: ${statusSummary.hardMissing[0].title}`;
  }
  if (statusSummary.consentGaps.length > 0) {
    return `Freigabe einholen: ${statusSummary.consentGaps[0].title}`;
  }
  if (statusSummary.pendingReview.length > 0) {
    return `Pruefung abschliessen: ${statusSummary.pendingReview[0].title}`;
  }
  if (statusSummary.packWarnings > 0) {
    return filing?.pack_ops_state?.next_recommended_action?.label
      || filing?.submission_pack_state?.next_recommended_action?.label
      || "Pack-Warnungen bereinigen";
  }
  const consentPending = requirements.find((entry) => entry.needsConsent && entry.consentStatus === "requested");
  if (consentPending) {
    return `Consent nachfassen: ${consentPending.title}`;
  }
  return filing?.submission_pack_state?.next_recommended_action?.label
    || filing?.pack_ops_state?.next_recommended_action?.label
    || "Case final pruefen";
}

function languageGapLabel(detail) {
  const alignment = detail?.decision_handoff?.language_evidence_alignment || {};
  if (alignment?.contradiction_flag) return "Widerspruch";
  if (alignment?.staleness_flag) return "Veraltet";
  const state = String(alignment?.alignment_state || "").toLowerCase();
  if (state === "no_shared_language_evidence") return "Fehlt";
  if (state === "insufficient_comparable_evidence") return "Unklar";
  return "Stabil";
}

function recognitionLabelFromRequirements(requirements) {
  return requirements.find((entry) => entry.key === "recognition_status")?.recognitionLabel || "nicht erforderlich";
}

export function buildCaseModelFromSources({ row, detail, filing, ad, gateSummary = null }) {
  const nextRow = row || {};
  const nextDetail = unwrapDetail(detail) || null;
  const nextFiling = unwrapFiling(filing) || null;
  const nextAd = ad || null;
  const classification = nextFiling?.classification || {};
  const candidateRef = nextDetail?.candidate_ref
    || nextDetail?.decision_handoff?.candidate_ref
    || nextRow?.candidate_ref
    || "CAND-pending";
  const adId = nextRow?.anzeigen_id || nextRow?.ad_id || nextDetail?.ad_id || nextAd?.id || null;
  const adTitle = nextAd?.titel || nextAd?.berufsbezeichnung || nextAd?.beruf_text || `Anzeige #${adId || "n/a"}`;
  const applicationStatus = nextDetail?.application_status || nextRow?.status || "new";
  const filingStage = nextFiling?.submission_pack_state?.current_stage_label
    || nextFiling?.review_workflow?.overall_label
    || (classification.fast_track ? "Filing aktiv" : "Case offen");

  const requirements = buildRequirementBlueprint({
    ad: nextAd,
    detail: nextDetail,
    filing: nextFiling,
    row: nextRow,
  });
  const statusSummary = summarizeCaseStatus({
    requirements,
    filing: nextFiling,
    detail: nextDetail,
    row: nextRow,
  });
  const nextAction = buildNextAction(statusSummary, nextFiling, requirements);
  const scorecard = buildScorecard(requirements, statusSummary.caseStatus, nextDetail || nextRow);
  const requiredDocs = requirements.filter((entry) => entry.required);
  const documentsReady = requiredDocs.filter((entry) => ["approved", "not_required"].includes(entry.status)).length;
  const visibleOriginalDocuments = requirements.filter((entry) => entry.availableOriginal).length;
  const gateLabel = gateSummary?.statusLabel || "";

  return {
    id: `${nextRow?.id || candidateRef}`,
    adId,
    applicationId: nextRow?.id || null,
    candidateRef,
    adTitle,
    applicationStatus,
    applicationStatusLabel: formatDecisionState(applicationStatus),
    filingStage,
    caseStatus: statusSummary.caseStatus,
    caseStatusLabel: statusSummary.caseStatusLabel,
    caseStatusColor: statusSummary.caseStatusColor,
    blockerCount: statusSummary.hardMissing.length + statusSummary.consentGaps.length + statusSummary.packBlockers,
    warningCount: statusSummary.pendingReview.length + statusSummary.packWarnings,
    blockerReasons: statusSummary.blockerReasons,
    nextAction,
    acceleratedProcedure: Boolean(classification.fast_track),
    regulatedProfession: Boolean(classification.regulated_profession),
    recognitionStatus: recognitionLabelFromRequirements(requirements),
    recognitionStatusLabel: recognitionLabelFromRequirements(requirements),
    documentRequirements: requirements,
    mandatoryRequirementCount: requiredDocs.length,
    missingRequirementCount: requiredDocs.filter((entry) => entry.status === "missing").length,
    requestedRequirementCount: requiredDocs.filter((entry) => entry.status === "requested").length,
    uploadedRequirementCount: requiredDocs.filter((entry) => entry.status === "uploaded").length,
    inReviewRequirementCount: requiredDocs.filter((entry) => entry.status === "in_review").length,
    approvedRequirementCount: requiredDocs.filter((entry) => entry.status === "approved").length,
    insufficientRequirementCount: requiredDocs.filter((entry) => entry.status === "insufficient").length,
    expiredRequirementCount: requiredDocs.filter((entry) => entry.status === "expired").length,
    notRequiredRequirementCount: requirements.filter((entry) => entry.status === "not_required").length,
    consentGapCount: statusSummary.consentGaps.length,
    visibleOriginalDocuments,
    documentsReady,
    requiredDocumentsReadyPercent: requiredDocs.length ? Math.round((documentsReady / requiredDocs.length) * 100) : 100,
    scorecard,
    gateLabel,
    gateRiskLabel: gateLabel && gateLabel !== "Gate bereit" ? gateLabel : "",
    languageGap: languageGapLabel(nextDetail),
    updatedAt: nextDetail?.decision_preparation?.updated_at || nextRow?.updated_at || null,
    createdAt: nextRow?.created_at || null,
    row: nextRow,
    detail: nextDetail,
    filing: nextFiling,
    raw: {
      application: nextRow,
      detail: nextDetail,
      filing: nextFiling,
      ad: nextAd,
    },
  };
}

export function buildCandidateCaseRows({ ads = [], applications = [], detailsById = {}, filingById = {}, gateSummary = null }) {
  const adIndex = new Map(asArray(ads).map((entry) => [String(entry.id), entry]));

  return asArray(applications)
    .map((row) => buildCaseModelFromSources({
      row,
      detail: detailsById[String(row.id)] || null,
      filing: filingById[String(row.id)] || null,
      ad: adIndex.get(String(row.anzeigen_id || row.ad_id || "")) || asArray(ads)[0] || null,
      gateSummary,
    }))
    .sort((left, right) => {
      if (left.blockerCount !== right.blockerCount) return right.blockerCount - left.blockerCount;
      return new Date(right.updatedAt || 0).getTime() - new Date(left.updatedAt || 0).getTime();
    });
}

function buildSupplementalDocumentRows(caseRow) {
  const filing = caseRow.filing || {};
  const mappedTitles = new Set(caseRow.documentRequirements.map((entry) => normalizeToken(entry.title)));

  return asArray(filing.documents)
    .filter((entry) => !mappedTitles.has(normalizeToken(entry.title)))
    .map((entry, index) => {
      const status = applyFreshnessToStatus(mapReviewBucketToStatus(entry, 0), entry);
      const needsConsent = ["candidate", "both"].includes(String(entry.who_provides_it || "").toLowerCase());
      const consentStatus = deriveConsentStatus({
        needsConsent,
        matchedDocument: entry,
        status,
        activeRequestCount: 0,
      });
      return {
        id: `${caseRow.applicationId}-supplemental-${index}`,
        applicationId: caseRow.applicationId,
        caseId: caseRow.id,
        candidateRef: caseRow.candidateRef,
        adTitle: caseRow.adTitle,
        section: "Unterstuetzende Unterlagen",
        title: entry.title || "Dokument",
        status,
        statusLabel: formatDocumentStatusLabel(status),
        statusColor: getDocumentStatusColor(status),
        consentStatus,
        consentStatusLabel: formatConsentStatusLabel(consentStatus),
        consentStatusColor: getConsentStatusColor(consentStatus),
        availableOriginal: Boolean(hasOriginalAccess(entry) && (!needsConsent || consentStatus === "granted")),
        verificationStatus: entry.review_bucket ? `Review ${String(entry.review_bucket).replace(/_/g, " ")}` : "Zusatzdokument",
        responsibility: entry.who_provides_it || entry.responsibility?.key || "authority",
        critical: false,
        required: false,
        nextAction: status === "expired" ? "Dokument aktualisieren" : status === "in_review" ? "Pruefung abschliessen" : "Referenz dokumentieren",
        freshness: entry.freshness?.label || entry.freshness?.detail || "k.A.",
        reviewBucketLabel: entry.review_bucket
          ? String(entry.review_bucket).replace(/_/g, " ")
          : "Zusatzdokument",
        raw: entry,
        caseRow,
      };
    });
}

export function buildDocumentRows({ caseRows = [] }) {
  return asArray(caseRows)
    .flatMap((caseRow) => {
      const requirementRows = caseRow.documentRequirements.map((entry) => ({
        id: entry.id,
        applicationId: caseRow.applicationId,
        caseId: caseRow.id,
        candidateRef: caseRow.candidateRef,
        adTitle: caseRow.adTitle,
        section: entry.section,
        title: entry.title,
        status: entry.status,
        statusLabel: entry.statusLabel,
        statusColor: entry.statusColor,
        consentStatus: entry.consentStatus,
        consentStatusLabel: entry.consentStatusLabel,
        consentStatusColor: entry.consentStatusColor,
        availableOriginal: entry.availableOriginal,
        verificationStatus: entry.verificationStatus,
        responsibility: entry.responsibility,
        critical: entry.critical,
        required: entry.required,
        nextAction: entry.nextAction,
        freshness: entry.matchedDocument?.freshness?.label || entry.note,
        reviewBucketLabel: entry.matchedDocument?.review_bucket
          ? String(entry.matchedDocument.review_bucket).replace(/_/g, " ")
          : "Case requirement",
        raw: entry.matchedDocument || null,
        caseRow,
      }));
      return [...requirementRows, ...buildSupplementalDocumentRows(caseRow)];
    })
    .sort((left, right) => {
      if (left.required !== right.required) return left.required ? -1 : 1;
      const leftWeight = getDocumentStatusMeta(left.status).weight;
      const rightWeight = getDocumentStatusMeta(right.status).weight;
      if (leftWeight !== rightWeight) return leftWeight - rightWeight;
      return left.title.localeCompare(right.title, "de");
    });
}

export function buildComplianceRows({ caseRows = [] }) {
  return asArray(caseRows).map((entry) => ({
    id: entry.id,
    candidateRef: entry.candidateRef,
    adTitle: entry.adTitle,
    filingStage: entry.filingStage,
    readinessLabel: entry.caseStatusLabel,
    readinessColor: entry.caseStatusColor,
    blockerCount: entry.blockerCount,
    warningCount: entry.warningCount,
    missingDocumentCount: entry.missingRequirementCount + entry.insufficientRequirementCount + entry.expiredRequirementCount,
    consentGapCount: entry.consentGapCount,
    mandatoryRequirementCount: entry.mandatoryRequirementCount,
    languageGap: entry.languageGap,
    recognitionStatus: entry.recognitionStatusLabel,
    nextAction: entry.nextAction,
  }));
}

export function buildIntegrationRows({ caseRows = [], profile = {} }) {
  const owner = normalizeText(profile.integration_owner_name) || "Owner offen";
  return asArray(caseRows).map((entry) => ({
    id: entry.id,
    candidateRef: entry.candidateRef,
    adTitle: entry.adTitle,
    statusLabel: entry.caseStatusLabel,
    housingReady: profile.housing_support_confirmed ? "Bereit" : "Offen",
    salaryBridgeReady: profile.payroll_bridge_ready ? "Bereit" : "Offen",
    integrationOwner: owner,
    teamReady: profile.team_ready ? "Bereit" : "Offen",
    milestone: entry.caseStatus === "filing_ready" ? "Arrival vorbereiten" : "Gate und Filing stabilisieren",
    nextAction: profile.housing_support_confirmed && profile.payroll_bridge_ready && profile.team_ready
      ? entry.nextAction
      : "Gate-Luecken vor Arrival schliessen",
  }));
}

function countBy(items, resolveKey) {
  return asArray(items).reduce((accumulator, item) => {
    const key = resolveKey(item);
    accumulator[key] = toNumber(accumulator[key]) + 1;
    return accumulator;
  }, {});
}

export function buildAnalyticsSnapshot({ ads = [], caseRows = [], documentRows = [], profile = {} }) {
  const activeAds = asArray(ads).filter((entry) => ["active", "published"].includes(String(entry.status || "").toLowerCase())).length;
  const totalAds = asArray(ads).length;
  const totalCases = asArray(caseRows).length;
  const filingReadyCases = asArray(caseRows).filter((entry) => entry.caseStatus === "filing_ready").length;
  const blockerCases = asArray(caseRows).filter((entry) => ["incomplete", "unclear", "consent_missing"].includes(entry.caseStatus)).length;
  const visibleOriginalDocuments = asArray(documentRows).filter((entry) => entry.availableOriginal).length;
  const documentIssues = asArray(documentRows).filter((entry) => entry.required && ["missing", "insufficient", "expired"].includes(entry.status)).length;
  const averageDecisionDays = totalCases
    ? asArray(caseRows).reduce((sum, entry) => {
      const start = new Date(entry.createdAt || entry.updatedAt || Date.now()).getTime();
      const end = new Date(entry.updatedAt || Date.now()).getTime();
      const deltaDays = Math.max(0, (end - start) / (1000 * 60 * 60 * 24));
      return sum + deltaDays;
    }, 0) / totalCases
    : 0;

  const statusCounts = countBy(caseRows, (entry) => entry.caseStatusLabel);
  const caseStatusCounts = countBy(caseRows, (entry) => entry.caseStatus);
  const scoreDistribution = {
    strong: asArray(caseRows).filter((entry) => entry.scorecard.percent >= 85).length,
    stable: asArray(caseRows).filter((entry) => entry.scorecard.percent >= 60 && entry.scorecard.percent < 85).length,
    fragile: asArray(caseRows).filter((entry) => entry.scorecard.percent >= 35 && entry.scorecard.percent < 60).length,
    critical: asArray(caseRows).filter((entry) => entry.scorecard.percent < 35).length,
  };

  const funnelRows = asArray(ads).map((ad) => {
    const adRows = asArray(caseRows).filter((entry) => String(entry.adId) === String(ad.id));
    return {
      id: ad.id,
      adTitle: ad.titel || ad.berufsbezeichnung || "Anzeige",
      adStatus: formatAdStatusLabel(ad.status),
      totalApplications: adRows.length,
      reviewCount: adRows.filter((entry) => String(entry.applicationStatus || "").toLowerCase() === "review").length,
      shortlistCount: adRows.filter((entry) => ["shortlist", "hired"].includes(String(entry.applicationStatus || "").toLowerCase())).length,
      blockedCount: adRows.filter((entry) => ["incomplete", "unclear", "consent_missing"].includes(entry.caseStatus)).length,
      filingReadyCount: adRows.filter((entry) => entry.caseStatus === "filing_ready").length,
    };
  });

  return {
    totalAds,
    activeAds,
    totalCases,
    readyCases: filingReadyCases,
    filingReadyCases,
    blockerCases,
    documentIssues,
    visibleOriginalDocuments,
    averageDecisionDays,
    statusCounts,
    caseStatusCounts,
    scoreDistribution,
    funnelRows,
    gateSummary: buildGateSummary(profile),
  };
}
